// =============================================================================
// Phase 6: Natural Language Parser
// Parses natural language date/time expressions like "today", "last week", "last 7 days"
// =============================================================================

import { getDaysInMonth } from "./utils/calendar";
import {
  WEEKDAY_TO_CANONICAL,
  UNIT_TO_EXPRESSION,
  ROUND_UNITS,
  MONTH_TO_NUMBER,
} from "./utils/constants";

export interface NaturalLanguageOptions {
  pickerType?: "single" | "range";
}

export interface NaturalLanguageResult {
  type: string;
  // For relative keywords
  base?: string;
  // For expression type
  value?: string;
  // For weekday type
  day?: string;
  // For relative-weekday type
  direction?: string;
  // For time type
  hour?: number;
  minute?: number;
  // For range type
  from?: string;
  to?: string;
}

// Local aliases for clarity
const WEEKDAYS = WEEKDAY_TO_CANONICAL;
const UNIT_MAP = UNIT_TO_EXPRESSION;

/**
 * Parse natural language date/time expression
 */
export function parseNaturalLanguage(
  input: string,
  options: NaturalLanguageOptions = {}
): NaturalLanguageResult {
  const { pickerType = "single" } = options;
  const lower = input.toLowerCase().trim();

  // Try each pattern in order of specificity

  // "now" keyword
  if (lower === "now") {
    return { type: "keyword", value: "now" };
  }

  // Time keyword: noon
  if (lower === "noon") {
    return { type: "time", hour: 12, minute: 0 };
  }

  // Relative day keywords: today, yesterday, tomorrow
  const relativeDayResult = parseRelativeDay(lower, pickerType);
  if (relativeDayResult) return relativeDayResult;

  // "This" period patterns: this week/month/year
  const thisResult = parseThisPeriod(lower);
  if (thisResult) return thisResult;

  // "Last/Next N" patterns: last 7 days, next 30 days
  const lastNResult = parseLastN(lower);
  if (lastNResult) return lastNResult;

  // "Since" patterns: since yesterday, since last week
  const sinceResult = parseSincePattern(lower);
  if (sinceResult) return sinceResult;

  // "Last/Next weekday" patterns: last monday, next friday
  const relativeWeekdayResult = parseRelativeWeekday(lower);
  if (relativeWeekdayResult) return relativeWeekdayResult;

  // "Last/Next/Previous" period patterns: last week, next month
  const lastNextResult = parseLastNextPeriod(lower, pickerType);
  if (lastNextResult) return lastNextResult;

  // Relative time expressions: "an hour ago", "in a day"
  const relativeTimeResult = parseRelativeTime(lower);
  if (relativeTimeResult) return relativeTimeResult;

  // Weekday names: monday, tue, etc.
  const weekdayResult = parseWeekday(lower, pickerType);
  if (weekdayResult) return weekdayResult;

  // Month+year patterns: jan 2026, january 2026
  const monthYearResult = parseMonthYear(lower);
  if (monthYearResult) return monthYearResult;

  // Note: Year-only parsing (e.g., "2026") is handled in the main parser
  // after time parsing fails, to prioritize time parsing for 4-digit inputs
  // like "1345" → 13:45 instead of year 1345

  // No match
  return { type: "unknown" };
}

/**
 * Parse relative day keywords (today, yesterday, tomorrow)
 */
function parseRelativeDay(
  input: string,
  pickerType: string
): NaturalLanguageResult | null {
  const days = ["today", "yesterday", "tomorrow"];
  if (!days.includes(input)) return null;

  if (pickerType === "range") {
    return {
      type: "range",
      from: `${input} 00:00`,
      to: `${input} 23:59`,
    };
  }

  return { type: "relative", base: input };
}

/**
 * Parse "this" period patterns (this week, this month, this year)
 */
function parseThisPeriod(input: string): NaturalLanguageResult | null {
  const match = input.match(/^this\s+(week|month|year)$/);
  if (!match) return null;

  const period = match[1];
  const unit = UNIT_MAP[period];

  return {
    type: "range",
    from: `now/${unit}`,
    to: "now",
  };
}

/**
 * Parse "last/past/previous N" and "next N" patterns
 */
function parseLastN(input: string): NaturalLanguageResult | null {
  // Match: last/past/previous N unit(s)
  const lastMatch = input.match(
    /^(last|past|previous)\s+(\d+)\s+(second|seconds|minute|minutes|hour|hours|day|days|week|weeks|month|months|year|years)$/
  );
  if (lastMatch) {
    const n = parseInt(lastMatch[2], 10);
    const unit = UNIT_MAP[lastMatch[3]];
    return {
      type: "range",
      from: `now-${n}${unit}`,
      to: "now",
    };
  }

  // Match: next N unit(s)
  const nextMatch = input.match(
    /^next\s+(\d+)\s+(second|seconds|minute|minutes|hour|hours|day|days|week|weeks|month|months|year|years)$/
  );
  if (nextMatch) {
    const n = parseInt(nextMatch[1], 10);
    const unit = UNIT_MAP[nextMatch[2]];
    return {
      type: "range",
      from: "now",
      to: `now+${n}${unit}`,
    };
  }

  return null;
}

/**
 * Parse "since" patterns (since yesterday, since last week)
 */
function parseSincePattern(input: string): NaturalLanguageResult | null {
  // Match: since yesterday/today/tomorrow
  const sinceDayMatch = input.match(/^since\s+(yesterday|today|tomorrow)$/);
  if (sinceDayMatch) {
    return {
      type: "range",
      from: sinceDayMatch[1],
      to: "now",
    };
  }

  // Match: since last week/month/year
  const sinceLastMatch = input.match(/^since\s+last\s+(week|month|year)$/);
  if (sinceLastMatch) {
    const unit = UNIT_MAP[sinceLastMatch[1]];
    return {
      type: "range",
      from: `now-1${unit}`,
      to: "now",
    };
  }

  // Match: since [date] - pass through the date for further parsing
  const sinceDateMatch = input.match(/^since\s+(.+)$/);
  if (sinceDateMatch) {
    return {
      type: "range",
      from: sinceDateMatch[1],
      to: "now",
    };
  }

  return null;
}

/**
 * Parse "last/next weekday" patterns (last monday, next friday)
 */
function parseRelativeWeekday(input: string): NaturalLanguageResult | null {
  const match = input.match(/^(last|next)\s+(\w+)$/);
  if (!match) return null;

  const direction = match[1];
  const dayName = match[2].toLowerCase();

  if (!WEEKDAYS[dayName]) return null;

  return {
    type: "relative-weekday",
    direction,
    day: WEEKDAYS[dayName],
  };
}

/**
 * Parse "last/next/previous" period patterns (last week, next month)
 */
function parseLastNextPeriod(
  input: string,
  pickerType: string
): NaturalLanguageResult | null {
  const match = input.match(/^(last|next|previous)\s+(week|month|year)$/);
  if (!match) return null;

  const direction = match[1];
  const period = match[2];
  const unit = UNIT_MAP[period];

  // Range mode: return range type
  if (pickerType === "range") {
    if (direction === "last" || direction === "previous") {
      return {
        type: "range",
        from: `now-1${unit}`,
        to: "now",
      };
    } else {
      return {
        type: "range",
        from: "now",
        to: `now+1${unit}`,
      };
    }
  }

  // Single mode: return expression type
  if (direction === "last" || direction === "previous") {
    if (period === "week") {
      return { type: "expression", value: "now-7d" };
    }
    return { type: "expression", value: `now-1${unit}` };
  } else {
    if (period === "week") {
      return { type: "expression", value: "now+7d" };
    }
    return { type: "expression", value: `now+1${unit}` };
  }
}

/**
 * Parse relative time expressions ("an hour ago", "in a day")
 */
function parseRelativeTime(input: string): NaturalLanguageResult | null {
  // "an hour ago", "a day ago", "a week ago"
  const agoMatch = input.match(/^an?\s+(second|minute|hour|day|week|month|year)\s+ago$/);
  if (agoMatch) {
    const unit = UNIT_MAP[agoMatch[1]];
    return { type: "expression", value: `now-1${unit}` };
  }

  // "in an hour", "in a day"
  const inMatch = input.match(/^in\s+an?\s+(second|minute|hour|day|week|month|year)$/);
  if (inMatch) {
    const unit = UNIT_MAP[inMatch[1]];
    return { type: "expression", value: `now+1${unit}` };
  }

  // "N hours ago", "N days ago"
  const nAgoMatch = input.match(/^(\d+)\s+(second|seconds|minute|minutes|hour|hours|day|days|week|weeks|month|months|year|years)\s+ago$/);
  if (nAgoMatch) {
    const n = parseInt(nAgoMatch[1], 10);
    const unit = UNIT_MAP[nAgoMatch[2]];
    return { type: "expression", value: `now-${n}${unit}` };
  }

  // "in N hours", "in N days"
  const inNMatch = input.match(/^in\s+(\d+)\s+(second|seconds|minute|minutes|hour|hours|day|days|week|weeks|month|months|year|years)$/);
  if (inNMatch) {
    const n = parseInt(inNMatch[1], 10);
    const unit = UNIT_MAP[inNMatch[2]];
    return { type: "expression", value: `now+${n}${unit}` };
  }

  return null;
}

/**
 * Parse weekday names (monday, tue, etc.)
 */
function parseWeekday(
  input: string,
  pickerType: string
): NaturalLanguageResult | null {
  const day = WEEKDAYS[input];
  if (!day) return null;

  return {
    type: "weekday",
    day,
  };
}

/**
 * Parse month+year patterns (jan 2026, january 2026)
 */
function parseMonthYear(input: string): NaturalLanguageResult | null {
  // "jan 2026", "january 2026"
  const match = input.match(/^([a-z]+)\s+(\d{4})$/);
  if (match) {
    const monthName = match[1].toLowerCase();
    const month = MONTH_TO_NUMBER[monthName];
    const year = parseInt(match[2], 10);

    if (month) {
      const endDay = getDaysInMonth(month, year);
      const pad = (n: number) => n.toString().padStart(2, "0");
      return {
        type: "range",
        from: `${year}-${pad(month)}-01`,
        to: `${year}-${pad(month)}-${pad(endDay)}`,
      };
    }
  }

  return null;
}

